import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CoverPagesService } from '../../services/cover-pages.service';
import { CoverPage } from '../../models';
import { CoverPageDialogComponent } from './cover-page-dialog.component';
import { ConfirmDialogComponent } from '../shared/confirm-dialog.component';

@Component({
  selector: 'app-cover-pages',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatMenuModule,
    MatSnackBarModule,
    MatProgressSpinnerModule
  ],
  template: `
    <div class="cover-pages-container">
      <div class="page-header">
        <h1>Gestión de Portadas</h1>
        <button mat-raised-button color="primary" (click)="openDialog()">
          <mat-icon>add</mat-icon>
          Nueva Portada
        </button>
      </div>

      <div *ngIf="loading" class="loading-container">
        <mat-spinner></mat-spinner>
      </div>

      <div class="cover-pages-grid" *ngIf="!loading">
        <mat-card *ngFor="let coverPage of coverPages" class="cover-page-card">
          <mat-card-header>
            <div mat-card-avatar class="cover-page-icon">
              <mat-icon>image</mat-icon>
            </div>
            <mat-card-title>{{ getMonthName(coverPage.month) }} {{ coverPage.year }}</mat-card-title>
            <mat-card-subtitle>{{ formatFileSize(coverPage.fileSize) }}</mat-card-subtitle>
            <button mat-icon-button [matMenuTriggerFor]="menu" class="card-menu">
              <mat-icon>more_vert</mat-icon>
            </button>
            <mat-menu #menu="matMenu">
              <button mat-menu-item (click)="openDialog(coverPage)">
                <mat-icon>edit</mat-icon>
                <span>Editar</span>
              </button>
              <button mat-menu-item (click)="deleteCoverPage(coverPage)">
                <mat-icon>delete</mat-icon>
                <span>Eliminar</span>
              </button>
            </mat-menu>
          </mat-card-header>

          <mat-card-content>
            <div class="cover-page-preview">
              <img *ngIf="!failedImages.has(coverPage.id)"
                   [src]="coverPage.fileUrl"
                   [alt]="'Portada ' + coverPage.month + '/' + coverPage.year"
                   (error)="onImageError(coverPage.id)" />
              <div *ngIf="failedImages.has(coverPage.id)" class="image-placeholder">
                <mat-icon>image_not_supported</mat-icon>
              </div>
            </div>
            <div class="cover-page-info">
              <p class="file-name">{{ coverPage.fileName }}</p>
              <p class="content-type">{{ coverPage.contentType }}</p>
            </div>
          </mat-card-content>

          <mat-card-actions>
            <button mat-button color="primary" (click)="previewImage(coverPage)">
              <mat-icon>visibility</mat-icon>
              Ver Imagen
            </button>
            <a [href]="coverPage.fileUrl" target="_blank" mat-button>
              <mat-icon>download</mat-icon>
              Descargar
            </a>
          </mat-card-actions>
        </mat-card>

        <mat-card *ngIf="coverPages.length === 0" class="empty-state">
          <mat-card-content>
            <div class="empty-state-content">
              <mat-icon class="empty-icon">image</mat-icon>
              <h3>No hay portadas registradas</h3>
              <p>Haz clic en "Nueva Portada" para agregar una.</p>
            </div>
          </mat-card-content>
        </mat-card>
      </div>
    </div>
  `,
  styles: [`
    .cover-pages-container {
      max-width: 1400px;
      margin: 0 auto;
    }

    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;

      h1 {
        font-family: var(--font-display);
        font-size: 28px;
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--text-primary);
        margin: 0;
      }
    }

    .loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 60px 0;
    }

    .cover-pages-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
      gap: 24px;
    }

    .cover-page-card {
      position: relative;
      background: var(--surface-color);
      border: 1px solid rgba(0, 0, 0, 0.05);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-sm);
      transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: var(--shadow-md);
      }

      mat-card-header {
        position: relative;
      }

      .card-menu {
        position: absolute;
        top: 8px;
        right: 8px;
      }
    }

    .cover-page-icon {
      width: 40px;
      height: 40px;
      border-radius: var(--radius-md);
      background: rgba(183, 28, 28, 0.1);
      display: flex;
      align-items: center;
      justify-content: center;

      mat-icon {
        color: var(--primary-color);
      }
    }

    .cover-page-preview {
      width: 100%;
      height: 200px;
      overflow: hidden;
      background: rgba(0, 0, 0, 0.04);
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 16px;
      border-radius: var(--radius-sm);

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.3s ease;

        &:hover {
          transform: scale(1.05);
        }
      }

      .image-placeholder {
        width: 64px;
        height: 64px;
        border-radius: var(--radius-md);
        background: rgba(183, 28, 28, 0.1);
        color: var(--primary-color);
        display: flex;
        align-items: center;
        justify-content: center;

        mat-icon {
          font-size: 32px;
          width: 32px;
          height: 32px;
        }
      }
    }

    .cover-page-info {
      p {
        margin: 4px 0;
      }

      .file-name {
        font-size: 14px;
        color: var(--text-primary);
        font-weight: 500;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .content-type {
        font-size: 12px;
        color: var(--text-secondary);
      }
    }

    mat-card-actions {
      padding: 8px 16px 16px;

      a {
        text-decoration: none;
      }
    }

    .empty-state {
      grid-column: 1 / -1;
      text-align: center;
      background: var(--surface-color);
      border: 1px solid rgba(0, 0, 0, 0.05);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-sm);
    }

    .empty-state-content {
      padding: 40px 20px;

      .empty-icon {
        width: 64px;
        height: 64px;
        border-radius: var(--radius-md);
        background: rgba(183, 28, 28, 0.1);
        font-size: 32px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: var(--primary-color);
        margin-bottom: 16px;
      }

      h3 {
        font-family: var(--font-display);
        font-size: 20px;
        font-weight: 700;
        color: var(--text-primary);
        margin: 0 0 8px 0;
      }

      p {
        color: var(--text-secondary);
        margin: 0;
      }
    }
  `]
})
export class CoverPagesComponent implements OnInit {
  coverPages: CoverPage[] = [];
  loading = true;
  failedImages = new Set<number>();

  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private coverPagesService = inject(CoverPagesService);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit() {
    this.loadCoverPages();
  }

  loadCoverPages() {
    this.loading = true;
    this.coverPagesService.getAllCoverPages().subscribe({
      next: (coverPages) => {
        setTimeout(() => {
          this.coverPages = coverPages;
          this.loading = false;
          this.cdr.detectChanges();
        }, 0);
      },
      error: () => {
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  getMonthName(month: number): string {
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return months[month - 1] || '';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  onImageError(id: number) {
    this.failedImages.add(id);
    this.cdr.markForCheck();
  }

  previewImage(coverPage: CoverPage) {
    window.open(coverPage.fileUrl, '_blank');
  }

  openDialog(coverPage?: CoverPage) {
    const dialogRef = this.dialog.open(CoverPageDialogComponent, {
      width: '500px',
      data: { coverPage }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        if (coverPage) {
          this.coverPagesService.updateCoverPage(coverPage.id, result).subscribe({
            next: (updatedCoverPage) => {
              this.coverPages = this.coverPages.map(cp => cp.id === coverPage.id ? updatedCoverPage : cp);
              this.failedImages.delete(updatedCoverPage.id);
              this.cdr.markForCheck();
              this.snackBar.open('Portada actualizada correctamente', 'Cerrar', { duration: 3000 });
            },
            error: () => {
              this.snackBar.open('Error al actualizar la portada', 'Cerrar', { duration: 3000 });
            }
          });
        } else {
          this.coverPagesService.createCoverPage(result).subscribe({
            next: (newCoverPage) => {
              this.coverPages = [...this.coverPages, newCoverPage];
              this.cdr.markForCheck();
              this.snackBar.open('Portada creada correctamente', 'Cerrar', { duration: 3000 });
            },
            error: () => {
              this.snackBar.open('Error al crear la portada', 'Cerrar', { duration: 3000 });
            }
          });
        }
      }
    });
  }

  deleteCoverPage(coverPage: CoverPage) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Eliminar Portada',
        message: `¿Estás seguro de que deseas eliminar la portada de ${this.getMonthName(coverPage.month)} ${coverPage.year}?`,
        confirmText: 'Eliminar',
        cancelText: 'Cancelar',
        type: 'danger'
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.coverPagesService.deleteCoverPage(coverPage.id).subscribe(() => {
          this.coverPages = this.coverPages.filter(cp => cp.id !== coverPage.id);
          this.snackBar.open('Portada eliminada correctamente', 'Cerrar', { duration: 3000 });
        });
      }
    });
  }
}
